import { useCallback, useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GrowHeader, GrowFooter } from "@/components/site/GrowHeader";
import { useCertifications } from "@/hooks/useCertifications";
import { personalTiers, pricingFootnote } from "@/content/personal-pricing";
import trainerAssetSrc from "@/assets/trainer.jpg";
const trainerAsset = { url: trainerAssetSrc };
import growHeroPersonSrc from "@/assets/grow-hero-person.png";
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import contactCallSrc from "@/assets/contact-call.jpg";
import { SITE_URL } from "@/lib/seo";
import { courses as allCourses } from "@/content/courses";

const normTitle = (s: string) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, " ").trim();
const coursesByTitle = new Map(allCourses.map((c) => [normTitle(c.title), c]));
/** Finds the catalog course matching a Training Pipeline course name. */
function findCatalogCourse(name: string) {
  return coursesByTitle.get(normTitle(name));
}

const TITLE = "Leadership Courses for Managers Who Are Struggling | Leadership Edge Live";
const DESCRIPTION =
  "Short, live leadership courses for one person at a time. Real facilitators, not AI. Start with one course for $49.";

export const Route = createFileRoute("/grow")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/grow/` }],
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://leadership-edge-live-v2.lovable.app/grow" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
  }),
  component: GrowPage,
});

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const primaryBtn = `inline-flex min-h-11 w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-center text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 sm:w-auto ${focusRing}`;
const secondaryBtn = `inline-flex min-h-11 w-full items-center justify-center rounded-[6px] border border-primary/25 bg-background/70 px-6 py-3 text-center text-sm font-semibold text-primary transition-colors hover:bg-lavender sm:w-auto ${focusRing}`;

const realPeoplePoints = [
  {
    title: "Live, Instructor-led Sessions",
    body: "Thirty minutes live each week with a real facilitator. If you are unavailable during your session, you can watch a full video replay.",
  },
  {
    title: "Support from an Expert",
    body: "Your Development Pro assigned to you will be there to make course recommendations and solve problems.",
  },
  {
    title: "No AI Slop",
    body: "We are experts in learning and leadership development with one goal... help you!",
  },
];

/** Maps a pricing tier to the plan preselected on /start. */
const tierPlans: Record<string, string> = {
  "One course": "one-course",
  Certification: "certification",
  Unlimited: "unlimited",
};

const AUTO_ADVANCE_MS = 4000;
/** Pause after a manual arrow click before auto-advance resumes. */
const MANUAL_PAUSE_MS = 6000;

function GrowPage() {
  const certScroller = useRef<HTMLDivElement>(null);
  const hoveringRef = useRef(false);
  const resumeAtRef = useRef(0);
  const { certifications, loading: certsLoading, error: certsError } = useCertifications();
  const certCountRef = useRef(0);
  certCountRef.current = certifications.length;

  /** Width of one card plus the flex gap. */
  const stepWidth = (el: HTMLDivElement) => {
    const card = el.querySelector("article");
    return (card?.clientWidth ?? 300) + 20;
  };

  /** Width of one full (non-duplicated) set of cards. */
  const setWidth = (el: HTMLDivElement) => stepWidth(el) * Math.max(certCountRef.current, 1);

  const step = useCallback((dir: -1 | 1) => {
    const el = certScroller.current;
    if (!el) return;
    const s = stepWidth(el);
    const loop = setWidth(el);
    // Silent, instant reposition into the equivalent point of the real set,
    // done only while the visible content is identical (duplicated cards).
    if (dir === 1 && el.scrollLeft >= loop - 1) {
      el.scrollTo({ left: el.scrollLeft - loop, behavior: "instant" as ScrollBehavior });
    } else if (dir === -1 && el.scrollLeft < s) {
      el.scrollTo({ left: el.scrollLeft + loop, behavior: "instant" as ScrollBehavior });
    }
    el.scrollBy({ left: dir * s, behavior: "smooth" });
  }, []);

  const scrollCerts = (dir: -1 | 1) => {
    resumeAtRef.current = Date.now() + MANUAL_PAUSE_MS;
    step(dir);
  };

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      if (hoveringRef.current) return;
      if (Date.now() < resumeAtRef.current) return;
      step(1);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [step]);

  return (
    <div className="relative flex min-h-dvh flex-col bg-background">
      <GrowHeader transparent />
      <main id="grow-main" className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[78vh] overflow-hidden md:min-h-[88vh]">
          <img
            src={boardroomAsset.url}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
          <div
            className="absolute inset-0 z-10 bg-gradient-to-r from-black/80 via-black/45 to-transparent"
            aria-hidden="true"
          />

          <div className="pointer-events-none absolute bottom-[150px] right-[210px] z-[5] hidden h-[49.6vh] max-h-[416px] w-[25.6%] md:block">
            <img
              src={growHeroPersonSrc}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-contain object-bottom"
            />
          </div>

          <div className="relative z-20 mx-auto flex min-h-[78vh] max-w-5xl flex-col justify-end px-5 pb-[146px] pt-[calc(5rem+80px)] sm:px-6 md:min-h-[88vh] md:pb-[178px]">
            <p className="mb-5 text-xs font-semibold uppercase tracking-wider text-white/80">
              High-impact learning that works for you
            </p>
            <h1
              style={{ fontFamily: "'Poppins', sans-serif" }}
              className="max-w-3xl text-[2rem] font-bold leading-[1.15] tracking-tight text-white md:text-[3.25rem]"
            >
              Nobody taught you
              <br />
              people skills. We can.
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6 md:mt-10">
              <Link
                to="/start/"
                search={{ plan: "one-course" }}
                className="inline-flex items-center rounded-[6px] bg-[#693D94] px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                Start with one course — $49
              </Link>
              <Link
                to="/schedule/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors hover:text-white"
              >
                See upcoming dates
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5 sm:px-6">
            <p className="text-center text-xs font-semibold uppercase tracking-wider text-primary">
              Certifications
            </p>
            <h2
              className="mt-3 text-center text-3xl font-semibold leading-tight text-ink md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Which one is you right now?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base leading-relaxed text-muted-foreground">
              Pick the problem you're facing. Three courses, one certificate you can put on your
              resume and your profile.
            </p>

            {certsLoading ? (
              <div className="mt-10 flex gap-5 overflow-hidden" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex w-[300px] shrink-0 flex-col rounded-2xl border-2 border-border/60 bg-background p-6 sm:w-[308px]"
                  >
                    <div className="mx-auto h-3 w-24 animate-pulse rounded bg-lavender" />
                    <div className="mx-auto mt-4 h-5 w-48 animate-pulse rounded bg-lavender" />
                    <div className="mt-4 space-y-2">
                      <div className="h-3 w-full animate-pulse rounded bg-lavender" />
                      <div className="h-3 w-5/6 animate-pulse rounded bg-lavender" />
                    </div>
                    <div className="mt-6 space-y-3 border-t border-border/60 pt-4">
                      <div className="h-3 w-4/5 animate-pulse rounded bg-lavender" />
                      <div className="h-3 w-3/5 animate-pulse rounded bg-lavender" />
                      <div className="h-3 w-4/6 animate-pulse rounded bg-lavender" />
                    </div>
                    <div className="mt-8 h-11 w-full animate-pulse rounded-[6px] bg-lavender" />
                  </div>
                ))}
                <span className="sr-only">Loading certifications</span>
              </div>
            ) : certsError || certifications.length === 0 ? (
              <div className="mx-auto mt-10 max-w-xl rounded-2xl border-2 border-border/60 bg-background p-7 text-center">
                <p className="text-base leading-relaxed text-muted-foreground">
                  {certsError ??
                    "Certifications aren't available to view right now. Talk to a Development Pro and we'll walk you through them."}
                </p>
                <Link to="/contact/" className={`mt-6 ${secondaryBtn} sm:w-auto`}>
                  Talk to a Development Pro
                </Link>
              </div>
            ) : (
              <div className="mt-10 flex items-center gap-4">
                <button
                  type="button"
                  aria-label="Scroll certifications left"
                  onClick={() => scrollCerts(-1)}
                  className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-border/60 bg-background text-ink shadow-brand transition-colors hover:bg-lavender max-lg:hidden ${focusRing}`}
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>

                <div
                  ref={certScroller}
                  onMouseEnter={() => {
                    hoveringRef.current = true;
                  }}
                  onMouseLeave={() => {
                    hoveringRef.current = false;
                  }}
                  className="flex min-w-0 flex-1 gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  {[...certifications, ...certifications].map((cert, idx) => {
                    const isClone = idx >= certifications.length;
                    return (
                      <article
                        key={`${cert.id}-${idx}`}
                        aria-hidden={isClone || undefined}
                        className="flex w-[300px] shrink-0 flex-col rounded-2xl border-2 border-border/60 bg-background p-6 transition-all hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] sm:w-[308px] sm:p-6"
                      >
                        <h3
                          className="mt-2 text-center text-xl font-semibold leading-snug text-ink"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {cert.name}
                        </h3>
                        {cert.description ? (
                          <div
                            className="cert-description mt-3 text-center text-sm leading-relaxed text-muted-foreground [&_p]:mt-2 [&_p:first-child]:mt-0"
                            dangerouslySetInnerHTML={{
                              __html: cert.description
                                .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
                                .replace(/(<br\s*\/?>\s*)+(<\/p>)?\s*$/i, "$2")
                                .trim(),
                            }}
                          />
                        ) : null}
                        {cert.courses.length ? (
                          <ol className="mt-3 space-y-2 border-t border-border/60 pt-4">
                            {cert.courses.map((course, i) => (
                              <li key={`${course.order}-${course.name}`} className="flex gap-3">
                                <span className="mt-2 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-lavender text-[11px] font-semibold text-[#693D94]">
                                  {i + 1}
                                </span>
                                <span className="flex min-h-9 flex-col justify-center">
                                  {(() => {
                                    const match = findCatalogCourse(course.name);
                                    return match ? (
                                      <Link
                                        to="/course-catalog/$slug/"
                                        params={{ slug: match.slug }}
                                        tabIndex={isClone ? -1 : undefined}
                                        className={`rounded-sm text-sm font-medium text-primary underline-offset-2 hover:underline ${focusRing}`}
                                      >
                                        {course.name}
                                      </Link>
                                    ) : (
                                      <span className="text-sm font-medium text-primary">
                                        {course.name}
                                      </span>
                                    );
                                  })()}
                                </span>
                              </li>
                            ))}
                          </ol>
                        ) : null}
                        <div className="mt-auto pt-6">
                          <Link
                            to="/start/"
                            search={{ plan: "certification", cert: String(cert.id) }}
                            tabIndex={isClone ? -1 : undefined}
                            className={`${primaryBtn} sm:w-full`}
                          >
                            Start Certification
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <button
                  type="button"
                  aria-label="Scroll certifications right"
                  onClick={() => scrollCerts(1)}
                  className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-border/60 bg-background text-ink shadow-brand transition-colors hover:bg-lavender max-lg:hidden ${focusRing}`}
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Real people */}
        <section className="relative isolate overflow-hidden py-16 text-primary-foreground md:py-24">
          <img
            src={trainerAsset.url}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 -z-20 h-full w-full object-cover"
          />
          <div className="absolute inset-0 -z-10 bg-black/80" aria-hidden="true" />
          <div className="relative mx-auto max-w-5xl px-5 sm:px-6">
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-primary-foreground md:whitespace-nowrap md:text-5xl">
              Real people on the other side
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-primary-foreground/80">
              You've probably already asked an AI what to say. It gave you a script that didn't fit.
              Here you sit in a live session with a facilitator who has experience managing people,
              alongside others in the same spot. A Development Pro learns your name and follows up.
            </p>
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {realPeoplePoints.map((point) => (
                <div
                  key={point.title}
                  className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/[0.06] p-6 sm:p-7"
                >
                  <h3 className="font-display text-lg font-bold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-primary-foreground/75">
                    {point.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="bg-lavender py-16 md:py-24">
          <div className="mx-auto max-w-5xl px-5 sm:px-6">
            <h2 className="font-display text-3xl leading-tight tracking-tight text-ink md:text-4xl">
              Pay for what you need
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {personalTiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`flex flex-col rounded-2xl border bg-background p-6 sm:p-7 ${
                    tier.highlighted
                      ? "border-primary/40 shadow-brand md:-translate-y-2"
                      : "border-border/60"
                  }`}
                >
                  {tier.highlighted && (
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary">
                      Most popular
                    </p>
                  )}
                  <h3 className="font-display text-xl text-ink">{tier.name}</h3>
                  <p className="mt-3 flex items-baseline gap-2">
                    <span className="font-display text-4xl text-ink">{tier.price}</span>
                    {tier.priceNote && (
                      <span className="text-sm text-muted-foreground">{tier.priceNote}</span>
                    )}
                  </p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {tier.body}
                  </p>
                  <Link
                    to="/start/"
                    search={{ plan: tierPlans[tier.name] ?? "certification" }}
                    className={`mt-6 ${tier.highlighted ? primaryBtn : secondaryBtn} sm:w-full`}
                  >
                    Get started
                  </Link>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs text-muted-foreground">{pricingFootnote}</p>

            <div className="mt-10 flex flex-col items-start gap-5 rounded-2xl border border-border/60 bg-background p-6 sm:p-7 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="font-display text-lg text-ink">
                  Using company development dollars?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  If your employer needs a purchase order, invoice, or other paperwork, we'll get it
                  to you.
                </p>
              </div>
              <Link to="/contact/" className={`${secondaryBtn} shrink-0`}>
                Contact a Development Pro
              </Link>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5 sm:px-6">
            <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
              <div className="text-left">
                <h2 className="font-display text-2xl font-bold leading-tight tracking-tight text-ink md:text-4xl">
                  I am uncertain training will solve this problem. It is too big...
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  Sometimes it isn't a training problem. If your boss is genuinely toxic, or the
                  workplace is broken, then a course probably won't fix it. We'd rather tell you
                  that than take your money. Connect with us and we can help.
                </p>
                <Link to="/contact/" className={`mt-7 ${primaryBtn}`}>
                  Talk to a Development Pro
                </Link>
              </div>
              <div className="overflow-hidden rounded-2xl border border-border/60">
                <img
                  src={contactCallSrc}
                  alt="Two people in a supportive one-on-one conversation"
                  loading="lazy"
                  className="h-full w-full object-cover md:aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
      <GrowFooter />
    </div>
  );
}
